import {fixPrice} from "./utils";
import apps, {SaleMode} from "./apps";
import {OrderStatus} from "./order_model";
import {Holiday} from "./hours";

/** shop **/
export interface ShopTblData {
    id: number,
    shopid: string,
    name: string,
    descr: string
    status: string,
    address: string,
    state?: string,   // 서버 State enum명("NSW"/"QLD"…). 서버 미배포 호환 위해 optional.
    callStaff?: boolean,   // QR Call Staff 버튼 노출 여부. 서버 미배포/구버전 sync 호환 위해 optional(기본 false).
    enableItemNote?: boolean,   // 항목별 메모 입력 노출 여부. 서버 미배포/구버전 sync 호환 위해 optional(기본 false).

    logoImageVer?: number,   // 스플래시 로고 캐시버스트 버전(서버 기본 1). 서버 미배포 시 undefined→1.

    // Inactivity Timeout(무활동 세션 초기화) — 서버 미배포/구버전 sync 호환 위해 optional.
    idleReset?: boolean,        // 기능 on/off (기본 false)
    idleMinutes?: number,       // 무활동 몇 분 후 경고 (기본 5)
    idleWarnSeconds?: number,   // 경고 표시 후 초기화까지 카운트다운 초 (기본 60)
    idleConfirmMinutes?: number, // 제출 시 장바구니가 몇 분 이상 오래됐으면 확인 (①, 기본 10)

    tblNo: string,
    qrType: string,

    happy1: boolean,
    happy1Descr: string,
    happy1St: Date,
    happy1End: Date,
    happy2: boolean,
    happy2Descr: string,
    happy2St: Date,
    happy2End: Date,
    openingTime: Date,
    closingTime: Date,
    monOpeningTime: Date,
    monClosingTime: Date,
    monOff: boolean,
    monBreakStart: number[] | null,
    monBreakEnd: number[] | null,
    tueOpeningTime: Date,
    tueClosingTime: Date,
    tueOff: boolean,
    tueBreakStart: number[] | null,
    tueBreakEnd: number[] | null,
    wedOpeningTime: Date,
    wedClosingTime: Date,
    wedOff: boolean,
    wedBreakStart: number[] | null,
    wedBreakEnd: number[] | null,
    thuOpeningTime: Date,
    thuClosingTime: Date,
    thuOff: boolean,
    thuBreakStart: number[] | null,
    thuBreakEnd: number[] | null,
    friOpeningTime: Date,
    friClosingTime: Date,
    friOff: boolean,
    friBreakStart: number[] | null,
    friBreakEnd: number[] | null,
    satOpeningTime: Date,
    satClosingTime: Date,
    satOff: boolean,
    satBreakStart: number[] | null,
    satBreakEnd: number[] | null,
    sunOpeningTime: Date,
    sunClosingTime: Date,
    sunOff: boolean,
    sunBreakStart: number[] | null,
    sunBreakEnd: number[] | null,
    holidays: Holiday[],
    signature: string,
    shopNotice: boolean,
    shopNoticeDescr: string,
    qrPaymentMethod: number,
    paymentRefId: string,
    cnpProvider?: "URPAY" | "TILL",   // CNP 카드결제 제공사. 서버 미배포 호환 위해 optional(기본 URPAY 취급).
    defaultPaymentType: string,
    cnpSurchargeRate: number,
    cnpFixedTransactionFee: number,
    holidaySurcharge: number,   // 공휴일 추가요금 %. 0 = 미적용
    orderedMsg: string,
    design: string | null,
    accentColor: string | null,
}

/** user **/
export interface UserData {
    id: number,
    uuid: string,
    email: string,
    mobile: string,
    status: UserStatus,
}

export enum UserStatus {
    DRAFT = "DRAFT",
    ACTIVE = "ACTIVE",
    HOLD = "HOLD",
    INACTIVE = "INACTIVE",
    QROFF = "QROFF",
}

/** Menu with Cate **/
export class CateMenuListData {
    cateId: number;
    cateName: string;
    alcohol?: boolean;   // true면 주류 카테고리 → 탭 시 주(state)별 법적 경고 이미지. 서버 미배포 호환 위해 optional.
    menus: MenuListData[];
}

export class MenuListData {
    id: number;
    menuid: number;
    name: string;
    descr: string;
    price: number;
    priceSp: number;
    special: boolean;
    soldOut: boolean;
    imageVer: number;
    vegetarian: boolean;
    gluetenFree: boolean;
    spicy: boolean;
    newItem: boolean;
    priceHappy1: number;
    priceHappy2: number;
    happy1Only: boolean;
    happy2Only: boolean;
    minOrd: number;
    maxOrd: number;
    optionIds: number[]; // tabletious option.id
    popular: boolean;    // 최근 주문 top10(🔥) — 서버 MenuService.active 집계
}

/** Option **/
export class OptionListData {
    name: string;
    optional: boolean;
    multiple: boolean;
    min: number;
    max: number;
    descr: string;
    items: OptionItemData[];
    maxPrice: number;
    optionId: number; // pikashop option.id
    optionid: number; // tabletious option.id. (option tag만들 때 사용)
}

export class OptionItemData {
    name: string;
    price: number;
    idx: number;
    optionItemId: number;   // pikashop option.id
    optionitemid: number;   // tabletious option.id. (option tag만들 때 사용)
}

/** CateMenu and Option **/
export class CateMenuAndOptionData {
    cateMenus: CateMenuListData[];
    options: OptionListData[];
}

/**
 * options map을 tag으로 변환
 * option사이 구분은 ,로 option과 optionItem 구분은 /로, optionItem사이 구분은 |로 한다
 * optionId1/optionItemId1|optionItemId2,optionId2/optionItemId1
 * **/
export function tagOptions(options: Map<OptionListData, OptionItemData[]>): string {
    const sorted: [OptionListData, OptionItemData[]][] =
        Array.from(options.entries())
            .sort(([o], [o2]) => o.optionid - o2.optionid)
            .map(([o, oi]) =>
                [o, oi.sort((a, b) => a.optionitemid - b.optionitemid)]);

    return sorted.map(([o, oi]) =>
        o.optionid + "|" + oi.map(i => i.optionitemid).join("/")).join(",");
}

/**
 * Cart
 * recoil state로 사용할 것이므로 상태를 직접 바꾸지 말고 새 오브젝트를 생성하면서 변경해야 한다
 * 안 그러면 recoil에서 사용할 때 preventObject 어쩌고 에러남
 **/
export class Cart {
    count: number = 0;
    items: CartItem[] = [];
    shop: ShopTblData;
    saleMode: SaleMode;

    constructor(saleMode: SaleMode, shop: ShopTblData, count: number = 0, items: CartItem[] = []) {
        this.shop = shop;
        this.count = count;
        this.items = items;
        this.saleMode = saleMode;
    }

    public updateShop(shop: ShopTblData) {
        return new Cart(this.saleMode, shop, this.count, this.items);
    }

    public updateSaleMode(saleMode: SaleMode) {
        return new Cart(saleMode, this.shop, this.count, this.items);
    }

    /**
     * 추가는 1개씩만. 기존 같은 아이템이 있으면 1씩 증가한다. 이후에 추가시 qty를 받을 수 있도록 qty 파라미터를 받아둔다.
     * note가 다르면 같은 메뉴+옵션이라도 별개 라인으로 취급한다(비병합 규칙, CartItem.tag() 참고). 빈 note끼리는 기존대로 병합.
     */
    public addItemWithShop(saleMode: SaleMode, menu: MenuListData, options: Map<OptionListData, OptionItemData[]>, qty: number, shop: ShopTblData, note: string = ""): Cart {
        const cart = (this.saleMode == SaleMode.UNKNOWN) ? this.updateSaleMode(saleMode) : this;
        return (this.shop != null) ? cart.addItem(menu, options, qty, note) : cart.updateShop(shop).addItem(menu, options, qty, note);
    }

    /**
     * 수정은 기존 아이템의 갯수만 업데이트한다. 만약 옵션을 변경한다면 다른 메뉴+옵션이라 추가로 처리된다.
     */
    public updateItemWithShop(menu: MenuListData, options: Map<OptionListData, OptionItemData[]>, qty: number, shop: ShopTblData, note: string = ""): Cart {
        return (this.shop != null) ? this.updateItem(menu, options, qty, note) : this.updateShop(shop).updateItem(menu, options, qty, note);
    }

    /**
     * 기존 item이 있으면 제거한 뒤에 add하는 방식으로 update한다.
     */
    public updateItem(menu: MenuListData, options: Map<OptionListData, OptionItemData[]>, qty: number, note: string = ""): Cart {
        const existing = this.items.find(item => item.isSame(menu, options, note));
        if (existing) {
            return this.removeItem(existing.tag()).addItem(menu, options, qty, note)
        } else {
            return this.addItem(menu, options, qty, note);
        }
    }

    public addItem(menu: MenuListData, options: Map<OptionListData, OptionItemData[]>, qty: number, note: string = ""): Cart {
        const existing = this.items.find(item => item.isSame(menu, options, note));
        if (existing) {
            const newItems = this.items.map((i: CartItem) => {
                if (i.isSame(menu, options, note)) return new CartItem(this.saleMode, i.menu, options, i.qty + qty, i.note);
                else return i;
            });
            return new Cart(this.saleMode, this.shop, this.count, newItems);
        } else {
            const newItems = [...this.items];
            newItems.push(new CartItem(this.saleMode, menu, options, qty, note));
            return new Cart(this.saleMode, this.shop, this.count + 1, newItems);
        }
    }

    public changeQty(menuId: number, options: Map<OptionListData, OptionItemData[]>, qty: number, note: string = "") {
        const existing = this.items.find(item => item.isSame2(menuId, options, note));
        if (existing) existing.qty = qty;
        return new Cart(this.saleMode, this.shop, this.count, this.items);
    }

    public removeItem(menuTag: string): Cart {
        let removed = 0;
        if (this.items.find(item => item.tag() == menuTag)) removed = 1;
        const newItems = this.items.filter(item => item.tag() != menuTag);
        return new Cart(this.saleMode, this.shop, this.count - removed, newItems);
    }

    public total() {
        return fixPrice(
            this.items.reduce((prev, cur) => {
                return prev + cur.itemPrice * cur.qty;
            }, 0));
    }

    /** 전체 주문 수량 합(라인별 qty 합산). count는 서로 다른 라인 수라, 배지엔 이 값을 쓴다. */
    public totalQty() {
        return this.items.reduce((prev, cur) => prev + cur.qty, 0);
    }
}

/**
 * cart.items를 FlatList에서 사용하기 편한 오브젝트로 재구성
 */
export const mapToItems = (items: CartItem[]) => {
    return items.map(i => {
        return {
            key: i.tag(),
            name: i.menu.name,
            menuid: i.menu.menuid,   // 주문 리스트 썸네일용
            imageVer: i.menu.imageVer,
            options: i.options,
            total: i.itemPrice,
            qty: i.qty,
            note: i.note,
            optionsDescr: () => {
                const ois: OptionItemData[] = [];
                Array.from(i.options.entries()).forEach(([o, oi]) => {
                    ois.push(...oi);
                });
                return ois.map(oi => oi.name).join(", ");
            },
        };
    });
};

export enum OrderType {
    TAKEAWAY = "TAKEAWAY",
    TABLE_QR = "TABLE_QR",
    TABLETIOUS_QR = "TABLETIOUS_QR",
    PAY_AT_COUNTER = "PAY_AT_COUNTER",
}

/**
 * 현재 준비중인 주문의 기본 정보
 * 주문이 시작되면(Add to order) YourOrder도 준비되야 함
 */
export class YourOrder {
    shopid: string;
    type: OrderType;
    qrType: string;    // Pikashop QR Type (3자리). OrderType을 더 세분화한 코드.
    tableNo: string;    // Table QR 또는 Tabletious QR인 경우만. Takeaway인 경우에도 tableNo를 사용할 수 있다.
    txId: number;      // Tabletious QR인 경우만
    payMethod: PayMethod;

    constructor(shopid: string = "", qrType: string, tableNo: string = "", txId: number = 0,
                payMethod: PayMethod = PayMethod.NOT_SELECTED) {
        this.shopid = shopid;
        this.type = this.findOrderType(qrType);
        this.qrType = qrType;
        this.tableNo = tableNo;
        this.txId = txId;
        this.payMethod = payMethod;
    }

    private findOrderType(qrType: string) {
        switch (qrType) {
            case "TAC": // takeaway with pay at counter
            case "TAP": // takeaway with fullpay when placing order
            case "TAA": // takeaway with combine all paymethods
                return OrderType.TAKEAWAY;
            case "TBC": // table-qr with pay at counter
            case "TBP": // table-qr with fullpay when placing order
                return OrderType.TABLE_QR;
            case "TQC": // tabletious-qr with pay at counter
                return OrderType.TABLETIOUS_QR;
            default:
                throw Error("Invalid QR Type: " + qrType);
        }
    }

    public getOrderInfo(): String {
        return (this.type == OrderType.TAKEAWAY) ? "TAKEAWAY" : `Table ${this.tableNo}`;
    }

    public updatePayMethod(payMethod: PayMethod): YourOrder {
        return new YourOrder(this.shopid, this.qrType, this.tableNo, this.txId, payMethod);
    }

    public getTable() {
        switch (this.type) {
            case OrderType.TAKEAWAY:
                return "TAKEAWAY";
            default:
                return `Table ${this.tableNo}`;
        }
    }

    getOrderInfoLong() {
        if (this.tableNo && this.tableNo.toLowerCase() === "menu") {
            return "Browsing MENU";
        }
        return (this.type == OrderType.TAKEAWAY) ? `TAKEAWAY (${this.tableNo})` : `DINING AT TABLE ${this.tableNo}`;
    }
}

export enum PayMethod {
    NOT_SELECTED = "NOT_SELECTED",
    CREDIT_CARD = "CREDIT_CARD",
    PAYPAL = "PAYPAL",
    STRIPE = "STRIPE",
    APPLE_PAY = "APPLE_PAY",
    PAY_AT_COUNTER = "PAY_AT_COUNTER",
}

export class CartItem {
    menu: MenuListData;
    options: Map<OptionListData, OptionItemData[]>;
    qty: number;
    itemPrice: number;  // menu.price + sum(selected_option_item.price)
    /**
     * tabletious로 보낼 SaleMode로 NORMAL, SPECIAL, HAPPY1, HAPPY2 중의 하나
     * apps.saleMode는 happyhour를 확인하기 위한 것이고, 이건 SPECIAL 적용여부가 추가된다.
     **/
    saleMode: SaleMode;
    note: string;   // 항목별 메모(선택, 최대 255자, trim된 값). 비병합 규칙: note가 다르면 별개 라인(tag() 참고).

    constructor(saleMode: SaleMode, menu: MenuListData, options: Map<OptionListData, OptionItemData[]>, qty: number = 1, note: string = "") {
        this.menu = menu;
        this.options = options;
        this.qty = qty;
        this.note = note;

        let itemPrice = apps.getPrice(saleMode, menu);
        options.forEach((optionItems, option) =>
            itemPrice += optionItems.reduce((acc, cur) => acc + cur.price, 0)
        );
        this.itemPrice = itemPrice;

        if (saleMode == SaleMode.NORMAL) {
            this.saleMode = menu.special ? SaleMode.SPECIAL : SaleMode.NORMAL;
        } else {
            this.saleMode = saleMode;
        }
    }

    tag(): string {
        return this.menu.id + "^" + tagOptions(this.options) + "^" + this.note;
    }

    increaseQty() {
        console.log(this.qty);
        this.qty++;
        console.log(this.qty);
    }

    isSame(other: MenuListData, otherOptions: Map<OptionListData, OptionItemData[]>, otherNote: string = "") {
        return this.menu.id == other.id &&
            tagOptions(this.options) == tagOptions(otherOptions) &&
            this.note == otherNote;
    }

    isSame2(otherMenuId: number, otherOptions: Map<OptionListData, OptionItemData[]>, otherNote: string = "") {
        return this.menu.id == otherMenuId &&
            tagOptions(this.options) == tagOptions(otherOptions) &&
            this.note == otherNote;
    }
}

export class Receipt {
    authorization: string;
    resultId: string;
    cardNumber: string;
    cardHolder: string;
    cardExpiry: string;
    cardToken: string;
    cardType: string;
    cardCategory: string;
    cardSubcategory: string;
    amount: number;
    decimalAmount: number;
    successful: boolean;
    message: string;
    reference: string;
    currency: string;
    transactionId: string;
    settlementDate: string;
    transactionDate: string;
    responseCode: string;
    captured: boolean;
    capturedAmount: number;
    rrn: string;
    cvvMatch: string;
    originalAmount: string;
    surchargeAmount: string;
    total: string;
    surchargeAmountDescription: string;

    authorizationTrackingId?: string;
    cardSequenceNumber?: string;
    scaExemption?: string;
    leastCostRouted?: string;
    originalTransactionReference?: string;
    serviceId?: string;
    trxSrc?: string;
    success: boolean;
    meaning: string;
    nextSteps?: string;

    shopId: number;
    orderId: number;

    constructor(
        authorization: string,
        resultId: string,
        cardNumber: string,
        cardHolder: string,
        cardExpiry: string,
        cardToken: string,
        cardType: string,
        cardCategory: string,
        cardSubcategory: string,
        amount: number,
        decimalAmount: number,
        successful: boolean,
        message: string,
        reference: string,
        currency: string,
        transactionId: string,
        settlementDate: string,
        transactionDate: string,
        responseCode: string,
        captured: boolean,
        capturedAmount: number,
        rrn: string,
        cvvMatch: string,
        originalAmount: string,
        surchargeAmount: string,
        total: string,
        surchargeAmountDescription: string,
        authorizationTrackingId: string,
        cardSequenceNumber: string,
        scaExemption: string,
        leastCostRouted: string,
        originalTransactionReference: string,
        serviceId: string,
        trxSrc: string,
        success: boolean,
        meaning: string,
        nextSteps: string,
        shopId: number,
        orderId: number
    ) {
        this.authorization = authorization;
        this.resultId = resultId;
        this.cardNumber = cardNumber;
        this.cardHolder = cardHolder;
        this.cardExpiry = cardExpiry;
        this.cardToken = cardToken;
        this.cardType = cardType;
        this.cardCategory = cardCategory;
        this.cardSubcategory = cardSubcategory;
        this.amount = amount;
        this.decimalAmount = decimalAmount;
        this.successful = successful;
        this.message = message;
        this.reference = reference;
        this.currency = currency;
        this.transactionId = transactionId;
        this.settlementDate = settlementDate;
        this.transactionDate = transactionDate;
        this.responseCode = responseCode;
        this.captured = captured;
        this.capturedAmount = capturedAmount;
        this.rrn = rrn;
        this.cvvMatch = cvvMatch;
        this.originalAmount = originalAmount;
        this.surchargeAmount = surchargeAmount;
        this.total = total;
        this.surchargeAmountDescription = surchargeAmountDescription;
        this.authorizationTrackingId = authorizationTrackingId;
        this.cardSequenceNumber = cardSequenceNumber;
        this.scaExemption = scaExemption;
        this.leastCostRouted = leastCostRouted;
        this.originalTransactionReference = originalTransactionReference;
        this.serviceId = serviceId;
        this.trxSrc = trxSrc;
        this.success = success;
        this.meaning = meaning;
        this.nextSteps = nextSteps;
        this.shopId = shopId;
        this.orderId = orderId;

    }
}

export class ReceiptRes {
    receiptId: number;
    receiptStatus: boolean;

    constructor(receiptId: number, receiptStatus: boolean) {
        this.receiptId = receiptId;
        this.receiptStatus = receiptStatus;
    }
}
